import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Switch from "../../components/ui/Switch";
import {
    createDiscountRequest,
    deleteDiscountRequest,
    getDiscountsRequest,
    updateDiscountRequest,
} from "../../services/api/event-content.api";
import { DiscountItem } from "../../services/types/event-content.types";

interface DiscountFormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    descriptionRu: string;
    descriptionEn: string;
    descriptionDe: string;
    descriptionUa: string;
    percent: string;
    requiresPaidCompanion: boolean;
    isActive: boolean;
}

const initialFormState: DiscountFormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    descriptionRu: "",
    descriptionEn: "",
    descriptionDe: "",
    descriptionUa: "",
    percent: "",
    requiresPaidCompanion: false,
    isActive: true,
};

const DiscountsPage: React.FC = () => {
    const [items, setItems] = useState<DiscountItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<DiscountFormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getDiscountsRequest();
            setItems(response);

            if (selectedId && !response.find((item) => item.id === selectedId)) {
                setSelectedId(null);
                setFormState(initialFormState);
            }
        } catch (error) {
            setPageError(
                error instanceof Error ? error.message : "Не удалось загрузить скидки.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            return (
                item.name_ru.toLowerCase().includes(query) ||
                item.name_en.toLowerCase().includes(query) ||
                item.name_de.toLowerCase().includes(query) ||
                (item.name_ua ?? "").toLowerCase().includes(query)
            );
        });
    }, [items, searchValue]);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: DiscountItem) => {
        setSelectedId(item.id);
        setFormState({
            nameRu: item.name_ru,
            nameEn: item.name_en,
            nameDe: item.name_de,
            nameUa: item.name_ua ?? "",
            descriptionRu: item.description_ru ?? "",
            descriptionEn: item.description_en ?? "",
            descriptionDe: item.description_de ?? "",
            descriptionUa: item.description_ua ?? "",
            percent: String(item.percent),
            requiresPaidCompanion: item.requires_paid_companion,
            isActive: item.is_active,
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleToggleActive = async (item: DiscountItem, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            const updated = await updateDiscountRequest(item.id, {
                is_active: !item.is_active,
            });
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
            if (selectedId === updated.id) {
                setFormState((prev) => ({ ...prev, isActive: updated.is_active }));
            }
        } catch (error) {
            setPageError(
                error instanceof Error ? error.message : "Не удалось изменить статус скидки.",
            );
        }
    };

    const handleDelete = async () => {
        if (!selectedId) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm("Удалить скидку безвозвратно?")) return;

        try {
            await deleteDiscountRequest(selectedId);
            setItems((prev) => prev.filter((item) => item.id !== selectedId));
            resetForm();
            setSuccessMessage("Скидка удалена.");
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Не удалось удалить скидку.",
            );
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Заполни название скидки на всех трёх языках.");
            return;
        }

        if (!formState.percent.trim()) {
            setFormError("Укажи размер скидки в процентах.");
            return;
        }

        const percent = Number(formState.percent);

        if (Number.isNaN(percent) || percent < 0 || percent > 100) {
            setFormError("Размер скидки должен быть числом от 0 до 100.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                name_ru: formState.nameRu.trim(),
                name_en: formState.nameEn.trim(),
                name_de: formState.nameDe.trim(),
                name_ua: formState.nameUa.trim() || null,
                description_ru: formState.descriptionRu.trim() || null,
                description_en: formState.descriptionEn.trim() || null,
                description_de: formState.descriptionDe.trim() || null,
                description_ua: formState.descriptionUa.trim() || null,
                percent,
                requires_paid_companion: formState.requiresPaidCompanion,
                is_active: formState.isActive,
            };

            if (selectedId) {
                const updated = await updateDiscountRequest(selectedId, payload);
                setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                handleSelect(updated);
                setSuccessMessage("Скидка обновлена.");
            } else {
                const created = await createDiscountRequest(payload);
                setItems((prev) => [created, ...prev]);
                handleSelect(created);
                setSuccessMessage("Скидка создана.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось сохранить скидку.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Скидки"
                description="Управление типами скидок: название на трёх языках, размер в процентах и описание, которое видит покупатель при выборе."
                actions={<Button onClick={resetForm}>Новая скидка</Button>}
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список скидок</h3>
                            <p className="card-text">
                                Выбери существующую скидку или создай новую.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по названию..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? <div className="form-global-error">{pageError}</div> : null}

                    {isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Скидки не найдены</h3>
                            <p className="empty-block-text">
                                Создай первую скидку или измени строку поиска.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Название</th>
                                    <th>Скидка</th>
                                    <th>Бесплатно + сопровождающий</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={
                                            item.id === selectedId ? "data-table-row-active" : ""
                                        }
                                        onClick={() => handleSelect(item)}
                                    >
                                        <td>
                                            <div className="table-primary-cell">
                                                <span className="table-primary-text">
                                                    {item.name_ru}
                                                </span>
                                                <span className="table-secondary-text">
                                                    {item.name_en} / {item.name_de}{item.name_ua ? ` / ${item.name_ua}` : ""}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{item.percent}%</td>
                                        <td>{item.requires_paid_companion ? "Да" : "—"}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`status-badge ${
                                                    item.is_active
                                                        ? "status-badge-active"
                                                        : "status-badge-inactive"
                                                }`}
                                                onClick={(event) => handleToggleActive(item, event)}
                                                title={
                                                    item.is_active
                                                        ? "Кликните, чтобы деактивировать"
                                                        : "Кликните, чтобы активировать"
                                                }
                                            >
                                                {item.is_active ? "Активна" : "Неактивна"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование скидки" : "Новая скидка"}
                            </h3>
                            <p className="card-text">
                                Заполни название и описание на русском, немецком и английском
                                языках, укажи размер скидки в процентах.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Название (RU)" htmlFor="discount-name-ru">
                                <Input
                                    id="discount-name-ru"
                                    value={formState.nameRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            nameRu: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Размер скидки, %" htmlFor="discount-percent">
                                <Input
                                    id="discount-percent"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step="0.01"
                                    value={formState.percent}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            percent: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Название (DE)" htmlFor="discount-name-de">
                                <Input
                                    id="discount-name-de"
                                    value={formState.nameDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            nameDe: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Название (UA)" htmlFor="discount-name-ua">
                                <Input
                                    id="discount-name-ua"
                                    value={formState.nameUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            nameUa: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Название (EN)" htmlFor="discount-name-en">
                                <Input
                                    id="discount-name-en"
                                    value={formState.nameEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            nameEn: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Описание (RU)" htmlFor="discount-description-ru">
                            <textarea
                                id="discount-description-ru"
                                className="ui-textarea"
                                rows={3}
                                value={formState.descriptionRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        descriptionRu: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Описание (DE)" htmlFor="discount-description-de">
                            <textarea
                                id="discount-description-de"
                                className="ui-textarea"
                                rows={3}
                                value={formState.descriptionDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        descriptionDe: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Описание (UA)" htmlFor="discount-description-ua">
                            <textarea
                                id="discount-description-ua"
                                className="ui-textarea"
                                rows={3}
                                value={formState.descriptionUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        descriptionUa: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Описание (EN)" htmlFor="discount-description-en">
                            <textarea
                                id="discount-description-en"
                                className="ui-textarea"
                                rows={3}
                                value={formState.descriptionEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        descriptionEn: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.requiresPaidCompanion}
                                onChange={(checked) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        requiresPaidCompanion: checked,
                                    }))
                                }
                                label="Билет бесплатный, но обязательно наличие сопровождающего с полной стоимостью билета"
                            />
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        isActive: checked,
                                    }))
                                }
                                label={formState.isActive ? "Активна" : "Неактивна"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? (
                            <div className="form-global-success">{successMessage}</div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохранение..."
                                    : selectedId
                                        ? "Сохранить изменения"
                                        : "Создать скидку"}
                            </Button>

                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Сбросить
                            </Button>

                            {selectedId ? (
                                <Button type="button" variant="secondary" onClick={handleDelete}>
                                    Удалить
                                </Button>
                            ) : null}
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default DiscountsPage;
